import React from 'react';
import { Card } from '@ui';

export type MetricChart = 'line' | 'bar' | 'donut' | 'area';

export type MetricDatum = {
  title: string;
  value: number;
  series: number[];
  baseline?: number;
  invert?: boolean;
  /** Upper bound for a donut. Defaults to 100. */
  max?: number;
};

type MetricCardsProps = {
  data?: MetricDatum[] | null;
  id?: string;
  className?: string;
  chart?: MetricChart;
  // sparkline sizing
  sparkWidth?: number;
  sparkHeight?: number;
  // show baseline if datum.baseline is provided
  showBaseline?: boolean;
};

const MetricCards: React.FC<MetricCardsProps> = ({
  data = null,
  id,
  className = 'metric-card-grid',
  chart = 'line',
  sparkWidth = 280,
  sparkHeight = 56,
  showBaseline = true,
}) => {
  function renderSpark(
    series: number[],
    invert = false,
    w = 280,
    h = 56,
    baseline?: number,
    areaOnly = false,
    up = true
  ) {
    const pad = 4;
    const max = Math.max(...series);
    const min = Math.min(...series);
    const X = (i: number) => pad + (i * (w - 2 * pad)) / (series.length - 1);
    const Y = (v: number) => {
      const t = (v - min) / (max - min || 1);
      return invert ? pad + t * (h - 2 * pad) : pad + (1 - t) * (h - 2 * pad);
    };

    const points = series.map((v, i) => `${i === 0 ? 'M' : 'L'}${X(i)},${Y(v)}`).join(' ');
    const dFill = `M${X(0)},${h - pad} ${points} L${X(series.length - 1)},${h - pad} Z`;
    const cls = up ? 'ok' : 'bad';

    return (
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className={`spark${areaOnly ? ' area' : ''} ${cls}`}
        preserveAspectRatio='none'
        aria-hidden='true'
      >
        {baseline !== undefined && (
          <line
            x1={pad}
            x2={w - pad}
            y1={Y(baseline)}
            y2={Y(baseline)}
            className='baseline'
            stroke='currentColor'
          />
        )}
        <path className='fill' d={dFill} fill='currentColor' />
        {areaOnly ? null : <path className='line' d={points} stroke='currentColor' fill='none' />}
      </svg>
    );
  }

  function renderBars(series: number[], w = 280, h = 56, up = true) {
    const pad = 4;
    const gap = 4;
    const max = Math.max(...series, 0);
    const barW = (w - pad * 2 - gap * (series.length - 1)) / series.length;
    const cls = up ? 'ok' : 'bad';

    return (
      <svg
        viewBox={`0 0 ${w} ${h}`}
        className={`spark bars ${cls}`}
        preserveAspectRatio='none'
        aria-hidden='true'
      >
        {series.map((v, i) => {
          const barH = (v / (max || 1)) * (h - pad * 2);
          const x = pad + i * (barW + gap);
          const y = h - pad - barH;
          return <rect key={i} x={x} y={y} width={Math.max(barW, 0)} height={barH} rx={2} />;
        })}
      </svg>
    );
  }

  function renderDonut(value: number, max = 100, up = true) {
    const size = 64;
    const stroke = 8;
    const radius = (size - stroke) / 2;
    const center = size / 2;
    const circumference = 2 * Math.PI * radius;
    const ratio = Math.max(0, Math.min(1, max ? value / max : 0));
    const dash = circumference * ratio;

    return (
      <svg viewBox={`0 0 ${size} ${size}`} className={`donut ${up ? 'ok' : 'bad'}`} aria-hidden='true'>
        <circle className='track' cx={center} cy={center} r={radius} />
        <circle
          className='arc'
          cx={center}
          cy={center}
          r={radius}
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
    );
  }

  return (
    <Card className={className} id={id ?? 'metricGrid'}>
      {data
        ? data.map((d, idx) => {
            const last = d.series[d.series.length - 1];
            const first = d.series[0];
            const change = last - first;
            const up = d.invert ? change < 0 : change >= 0;
            const pct = first ? Math.round((change / first) * 100) : 0;
            const cls = up ? 'up' : 'down';
            const sign = up ? '▲' : '▼';

            return (
              <Card key={idx} className='metric'>
                <div className='metric-top'>
                  <span className='metric-title'>{d.title}</span>
                  <span className={`change ${cls}`}>
                    {sign} {Math.abs(pct)}%
                  </span>
                </div>
                <div className='metric-value'>{d.value}</div>
                <div className='spark-placeholder'>
                  {chart === 'bar'
                    ? renderBars(d.series, sparkWidth, sparkHeight, up)
                    : chart === 'donut'
                      ? renderDonut(d.value, d.max ?? 100, up)
                      : renderSpark(
                          d.series,
                          !!d.invert,
                          sparkWidth,
                          sparkHeight,
                          showBaseline && chart === 'line' ? d.baseline : undefined,
                          chart === 'area',
                          up
                        )}
                </div>
              </Card>
            );
          })
        : [1, 2, 3].map((i) => (
            <Card key={i} className='metric'>
              <div>
                <span className='metric-title'>...</span>
                <span className='change'>…</span>
              </div>
              <div className='metric-value'>—</div>
              <div className='spark-placeholder' aria-hidden>
                {/* placeholder sparkline area */}
              </div>
            </Card>
          ))}
    </Card>
  );
};

export default MetricCards;
